<script lang="ts">
  /**
   * Keyboard shortcut reference. Display-only — not configurable. Opened by
   * the global "?" listener in App.svelte (also: any caller can flip the
   * exported `helpOpen` store). Closes on Esc, on click-outside, or via the
   * dismiss button.
   *
   * The modal uses the native <dialog> element so Esc-to-close and the
   * black backdrop come for free; matches the InventoryTab discard dialog
   * pattern. Click-outside is handled by intercepting clicks that land on
   * the dialog box itself (dialog::backdrop is technically the ::backdrop
   * pseudo, so a click landing on the <dialog> element with the form below
   * not intercepting means the player clicked the backdrop).
   */
  import { helpOpen } from './stores'

  let dialog: HTMLDialogElement | undefined = $state()

  $effect(() => {
    if ($helpOpen) dialog?.showModal()
    else dialog?.close()
  })

  function close() {
    helpOpen.set(false)
  }

  /** Click handler on the <dialog> itself. Inner content (the form) stops
   *  propagation, so a click that reaches here is by definition on the
   *  backdrop. */
  function onBackdropClick(ev: MouseEvent) {
    if (ev.target === dialog) close()
  }

  function onCancel(ev: Event) {
    ev.preventDefault()
    close()
  }

  interface Shortcut {
    keys: string[]
    description: string
  }

  interface Section {
    title: string
    shortcuts: Shortcut[]
  }

  const sections: Section[] = [
    {
      title: 'Editor',
      shortcuts: [
        { keys: ['Click'], description: 'Select a building' },
        { keys: ['Drag'], description: 'Move the selected building' },
        { keys: ['Delete'], description: 'Send selected building to inventory' },
        { keys: ['Backspace'], description: 'Send selected building to inventory' },
        { keys: ['Ctrl', 'D'], description: 'Duplicate selected building' },
        { keys: ['Ctrl', 'Z'], description: 'Undo last change' },
        { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo' },
        { keys: ['Ctrl', 'Y'], description: 'Redo (Windows alternative)' },
        { keys: ['↑', '↓', '←', '→'], description: 'Nudge selected building one tile' },
        { keys: ['Shift', '↑↓←→'], description: 'Nudge four tiles at once' },
        { keys: ['Esc'], description: 'Cancel pickup, gesture, tool, or selection' },
      ],
    },
    {
      title: 'Tools',
      shortcuts: [
        { keys: ['Hold R'], description: 'Switch to Road tool while held' },
        { keys: ['Hold S'], description: 'Switch to Store tool while held' },
        { keys: ['S', '+ Drag'], description: 'Sweep a region of buildings to inventory' },
      ],
    },
    {
      title: 'View',
      shortcuts: [
        { keys: ['F'], description: 'Fit city to the screen' },
        { keys: ['+'], description: 'Zoom in' },
        { keys: ['−'], description: 'Zoom out' },
        { keys: ['Mouse wheel'], description: 'Zoom in/out around the cursor' },
        { keys: ['Middle-click', '+ Drag'], description: 'Pan the canvas' },
      ],
    },
    {
      title: 'Help',
      shortcuts: [
        { keys: ['?'], description: 'Open this shortcut reference' },
      ],
    },
  ]
</script>

<dialog
  bind:this={dialog}
  oncancel={onCancel}
  onclick={onBackdropClick}
  class="w-[min(640px,92vw)] rounded-xl border border-border-rule bg-surface-card p-0 text-text-primary backdrop:bg-black/60"
>
  <form method="dialog" class="flex flex-col" onsubmit={(e) => e.preventDefault()}>
    <header class="flex items-center justify-between border-b border-border-rule px-5 py-3">
      <h2 class="text-base font-semibold">Keyboard shortcuts</h2>
      <button
        type="button"
        class="rounded p-1 text-text-secondary hover:bg-surface-card-recessed hover:text-text-primary"
        aria-label="Close shortcut reference"
        onclick={close}>✕</button
      >
    </header>

    <section class="grid max-h-[70vh] grid-cols-1 gap-x-6 gap-y-5 overflow-y-auto p-5 sm:grid-cols-2">
      {#each sections as section (section.title)}
        <div>
          <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
            {section.title}
          </h3>
          <ul class="flex flex-col gap-1.5 text-sm">
            {#each section.shortcuts as sc, i (i)}
              <li class="flex items-baseline justify-between gap-3">
                <span class="flex flex-wrap items-baseline gap-1">
                  {#each sc.keys as key, j (j)}
                    {#if j > 0 && !key.startsWith('+')}
                      <span class="text-text-muted">+</span>
                    {/if}
                    <kbd
                      class="rounded border border-border-rule bg-surface-card-recessed px-1.5 py-0.5 font-mono text-[11px] text-text-primary shadow-sm"
                      >{key}</kbd
                    >
                  {/each}
                </span>
                <span class="text-right text-xs text-text-secondary">{sc.description}</span>
              </li>
            {/each}
          </ul>
        </div>
      {/each}
    </section>

    <footer class="border-t border-border-rule px-5 py-3 text-[11px] text-text-muted">
      Press <kbd
        class="rounded border border-border-rule bg-surface-card-recessed px-1 py-0.5 font-mono text-[10px]"
        >Esc</kbd
      > or click outside to close.
    </footer>
  </form>
</dialog>

<style>
  dialog {
    margin: auto;
  }
</style>
